import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { get, set } from 'idb-keyval';
import { useCurrentPlans, useMarkShopped, useShoppingList } from '../api/planner';
import type { ListItem } from '../api/planner';
import ConfirmDialog from '../components/ConfirmDialog';
import RecipeThumb from '../components/RecipeThumb';
import ItemSwap from './review/ItemSwap';
import PantryStock from './PantryStock';

/** Ticked-off items live in IndexedDB: the aisle is where the wifi isn't. */
const CHECKED_KEY = 'tt-checked-items';

/** The dinners needing a line, tolerating the older shape.

    This used to be a bare list of titles. A service worker holding a list
    cached before the change would hand this page strings where it now expects
    objects, and the aisle is the worst place to find that out. */
function listRecipes(item: ListItem): { id: number; title: string }[] {
  return item.recipes.map((entry) =>
    typeof entry === 'string' ? { id: 0, title: entry } : entry,
  );
}

/** "You ticked 3 of 12 — which did you actually buy?"

    Deliberately has no default action: both answers are plausible and only
    the shopper knows which, so neither is pre-chosen and Escape backs out
    without stocking anything. */
function ConfirmPartial({
  ticked,
  total,
  onTicked,
  onEverything,
  onCancel,
}: {
  ticked: number;
  total: number;
  onTicked: () => void;
  onEverything: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDialog
      titleId="confirm-partial-title"
      title={`You ticked ${ticked} of ${total}`}
      onCancel={onCancel}
      actions={
        <>
          <button className="btn btn--primary" onClick={onEverything}>
            I bought everything ({total})
          </button>
          <button className="btn" onClick={onTicked}>
            Only the {ticked} I ticked
          </button>
          <button className="link-button" onClick={onCancel}>
            Back to the list
          </button>
        </>
      }
    >
      <p className="muted">
        The {total - ticked} unticked item{total - ticked === 1 ? '' : 's'} will
        not go into the pantry, so next week's plan will not count{' '}
        {total - ticked === 1 ? 'it' : 'them'} as being in the cupboard.
      </p>
    </ConfirmDialog>
  );
}

function useCheckedItems(planId: number | undefined) {
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!planId) return;
    void get<Record<number, number[]>>(CHECKED_KEY).then((stored) => {
      setChecked(new Set(stored?.[planId] ?? []));
      setLoaded(true);
    });
  }, [planId]);

  async function toggle(ingredientId: number) {
    const next = new Set(checked);
    if (next.has(ingredientId)) next.delete(ingredientId);
    else next.add(ingredientId);
    setChecked(next);
    if (!planId) return;
    const stored = (await get<Record<number, number[]>>(CHECKED_KEY)) ?? {};
    stored[planId] = [...next];
    await set(CHECKED_KEY, stored);
  }

  async function clear() {
    setChecked(new Set());
    if (!planId) return;
    const stored = (await get<Record<number, number[]>>(CHECKED_KEY)) ?? {};
    delete stored[planId];
    await set(CHECKED_KEY, stored);
  }

  return { checked, toggle, clear, loaded };
}

export default function ShoppingList() {
  const { data: plansPage } = useCurrentPlans();
  const plans = plansPage?.results ?? [];
  const activePlan = plans.find((plan) => plan.status === 'accepted') ?? plans[0];

  /* Only the exceptions. A blend this shop cannot sell is mixed at home by
     default — the week was chosen on the strength of that being possible —
     so holding the whole set here would make "the cook has said nothing" and
     "the cook unticked the only one" the same request. */
  const [blendChoice, setBlendChoice] = useState<Record<number, 'home' | 'buy'>>({});
  const blends = useMemo(
    () => ({
      home: Object.keys(blendChoice)
        .filter((id) => blendChoice[Number(id)] === 'home')
        .map(Number),
      buy: Object.keys(blendChoice)
        .filter((id) => blendChoice[Number(id)] === 'buy')
        .map(Number),
    }),
    [blendChoice],
  );
  const { data: list, isLoading } = useShoppingList(activePlan?.id, blends);
  const { checked, toggle, clear } = useCheckedItems(activePlan?.id);
  const shopped = useMarkShopped();
  // Set when a half-ticked list needs deciding before anything is stocked.
  const [confirming, setConfirming] = useState<{ ticked: number; total: number } | null>(
    null,
  );

  if (isLoading) return <p className="center-note">Building your list…</p>;
  if (!list) {
    return (
      <section>
        <h1>Shopping list</h1>
        <p className="muted">Accept a week's plan and the list appears here.</p>
      </section>
    );
  }

  const total = list.departments.reduce((sum, dept) => sum + dept.items.length, 0);

  /** Stock the pantry from this list. `ids` undefined means the whole thing. */
  function stock(ids?: number[]) {
    if (!list) return;
    setConfirming(null);
    shopped.mutate(
      {
        planId: list.plan_id,
        ingredientIds: ids,
        // A second shop for a week that was added to. The server refuses one
        // without this, so a page reopened in the aisle cannot stock the
        // cupboard twice by accident. Left off entirely on a first shop, the
        // way ingredientIds is: a body should carry what is being asked for,
        // not a field saying this is not the unusual case.
        again: list.shopped_at ? true : undefined,
        // What lands in the cupboard has to be what the list said to buy.
        blends,
      },
      { onSuccess: () => void clear() },
    );
  }

  /** Put the shop away, checking first if the ticks look accidental.

      Ticking is how you keep your place in the aisle, not a declaration of
      what you bought, so a half-ticked list is ambiguous in a way that costs
      real money either way: stock everything and the pantry claims food that
      is not there, stock only the ticks and next week re-buys what is. The
      one case that needs no question is nothing ticked at all — that is a
      list nobody kept score on, and the whole thing came home. */
  function putAway() {
    if (!list) return;
    const ticked = checked.size;
    if (ticked > 0 && ticked < total) {
      setConfirming({ ticked, total });
      return;
    }
    stock(ticked > 0 ? [...checked] : undefined);
  }

  /** Which way a blend goes, said outright rather than flipped.

      The button that made this call already knows the answer: a blend still
      on the list as a line of its own is one being bought, and the undo only
      exists on the components of one being made. So neither has to work out
      what the shop forced by default. */
  function chooseBlend(id: number, choice: 'home' | 'buy') {
    setBlendChoice((current) => ({ ...current, [id]: choice }));
  }

  return (
    <section>
      <h1>Shopping list</h1>

      {confirming && (
        <ConfirmPartial
          ticked={confirming.ticked}
          total={confirming.total}
          onTicked={() => stock([...checked])}
          onEverything={() => stock(undefined)}
          onCancel={() => setConfirming(null)}
        />
      )}

      {/* The total was buried mid-sentence in a muted line and had to be hunted
          for; it is the number you actually want off this page. */}
      <div className="list-head">
        <p className="muted">
          {list.store} · {list.servings} servings
          {/* One number would be a lie about a week where some dinner was
              re-sized, and the list below has already been built at the
              bigger figure. */}
          {list.servings_vary ? ' (some dinners differ)' : ''} · {total} item
          {total === 1 ? '' : 's'}
          {checked.size > 0 && ` · ${checked.size} ticked off`}
        </p>
        {list.estimated_total && (
          <p className="list-total">
            <span className="list-total__amount">${list.estimated_total}</span>
            <span className="muted small"> estimated</span>
          </p>
        )}
      </div>
      {list.sale_savings && (
        <p className="savings">Sales on this list save about ${list.sale_savings}</p>
      )}

      {list.departments.map((dept) => (
        <div key={dept.category} className="dept">
          <h2 className="dept__title">{dept.label}</h2>
          <ul className="checklist">
            {dept.items.map((item) => (
              <li key={item.ingredient_id}>
                <label className={checked.has(item.ingredient_id) ? 'is-checked' : ''}>
                  <input
                    type="checkbox"
                    checked={checked.has(item.ingredient_id)}
                    onChange={() => void toggle(item.ingredient_id)}
                  />
                  <span>
                    {/* What to put in the basket, not what will be used —
                        nobody sells half an orange. Falls back to the amount
                        needed where the pack size is unknown. */}
                    {item.buy_display ? (
                      <strong>{item.buy_display} </strong>
                    ) : (
                      /* What is left to buy, not what the recipes add up to:
                         a jar already half full only needs topping up. */
                      item.outstanding_display && (
                        <strong>
                          {item.outstanding_display} {item.unit}{' '}
                        </strong>
                      )
                    )}
                    {item.name}
                    {item.on_sale && <span className="tag tag--sale">sale</span>}
                    {item.unavailable && (
                      <span className="tag tag--warn">may need a substitute</span>
                    )}
                    {/* One punnet answering two dinners. Unexplained, the
                        amount looks wrong for the recipe named beside it. */}
                    {item.covers_names?.length ? (
                      <span className="item__need">
                        also covers {item.covers_names.join(', ')}
                      </span>
                    ) : null}
                    {/* Otherwise a handful of spices appears from nowhere,
                        with nothing to say they are one blend between them. */}
                    {item.homemade_for && (
                      <span className="item__need">
                        for the {item.homemade_for.name} you're making at home
                      </span>
                    )}
                    {/* Why this line is buying the thing it is. Without it a
                        second-best match looks like the matcher misfiring,
                        rather than the shop being out of the better one. */}
                    {item.passed_over_name && (
                      <span className="item__product">
                        closer match out of stock: {item.passed_over_name}
                      </span>
                    )}
                    {item.price && <span className="price">${item.price}</span>}
                    {item.buy_display && item.need_display && (
                      <span className="item__need">
                        recipes need {item.need_display}
                        {item.buy_is_estimate && ' · estimated'}
                      </span>
                    )}
                    {/* A line the cupboard partly covers stays on the list for
                        the difference, and has to say so — a smaller number
                        with no explanation reads as a bug in the aisle. */}
                    {!item.in_pantry && item.pantry_note && (
                      <span className="item__need">{item.pantry_note}</span>
                    )}
                    {item.product_name && (
                      <span className="item__product">
                        {item.product_url ? (
                          <a href={item.product_url} target="_blank" rel="noreferrer">
                            {item.product_name}
                          </a>
                        ) : (
                          item.product_name
                        )}
                        {item.product_size && ` · ${item.product_size}`}
                      </span>
                    )}
                    <span className="item__for">
                      for
                      {listRecipes(item).map((recipe) => (
                        <span key={`${recipe.id}-${recipe.title}`} className="item__recipe">
                          <RecipeThumb id={recipe.id} title={recipe.title} size="sm" />
                          {recipe.title}
                        </span>
                      ))}
                    </span>
                  </span>
                </label>
                <div className="item__actions">
                  {/* A blend still on the list as a line of its own is one
                      being bought: the made-at-home ones left as their parts. */}
                  {item.can_make_at_home && (
                    <button
                      className="link-button"
                      onClick={() => chooseBlend(item.ingredient_id, 'home')}
                    >
                      make it at home instead
                    </button>
                  )}
                  {/* The way back. Making a blend at home removes the line
                      whose button did this, so the undo has to live on the
                      parts it was replaced by. */}
                  {item.homemade_for && (
                    <button
                      className="link-button"
                      onClick={() => chooseBlend(item.homemade_for!.ingredient_id, 'buy')}
                    >
                      buy the {item.homemade_for.name} ready-made
                    </button>
                  )}
                  {/* A wrong match is silent — a jar of pumpkin butter costs
                      about what a pack of butter costs — and the place you
                      notice is the aisle, not the review page. */}
                  <ItemSwap
                    ingredientId={item.ingredient_id}
                    ingredientName={item.name}
                    storeId={list!.store_id}
                    storeName={list!.store}
                    productName={item.product_name}
                  />
                  {/* The other thing you find out in the aisle: it was in the
                      cupboard all along. */}
                  <PantryStock item={item} storeUnit={item.unit} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {list.already_have.length > 0 && (
        <div className="dept">
          <h2 className="dept__title">Already in the pantry</h2>
          <ul className="have-list">
            {list.already_have.map((item) => (
              <li key={item.ingredient_id}>
                {item.name} <span className="muted">— {item.pantry_note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="plan-actions">
        {/* The other half of the pantry. Cooking a dinner draws it down;
            nothing used to fill it up, so the planner's preference for
            recipes that use what you already have had nothing to work with. */}
        {list.shopped_at && (
          <p className="muted small">
            Put away on{' '}
            {new Date(list.shopped_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
            . What was left over is in the pantry, and next week's plan will
            try to use it up.
          </p>
        )}
        {/* Offered again when a week that was already shopped for still has
            lines on it. That happens when dinners were added to the week, or
            one was re-sized: the food for them is not in the cupboard, and
            without this there was no way to put it there. Nothing is stocked
            twice by saying so — everything from the first shop now reads as
            pantry stock, and putting the shopping away skips what the pantry
            already covers. */}
        {(!list.shopped_at || total > 0) && (
          <>
            <button
              className="btn btn--wide"
              disabled={shopped.isPending}
              onClick={putAway}
            >
              {shopped.isPending
                ? 'Stocking the pantry…'
                : list.shopped_at
                  ? 'Put the rest away →'
                  : 'Done shopping →'}
            </button>
            <p className="muted small">
              {list.shopped_at ? (
                <>
                  The {total} item{total === 1 ? '' : 's'} above{' '}
                  {total === 1 ? "wasn't" : "weren't"} covered by your last
                  shop — the dinners added since need{' '}
                  {total === 1 ? 'it' : 'them'}.
                </>
              ) : (
                <>
                  Puts{' '}
                  {checked.size > 0 ? 'the ticked items' : 'everything above'}{' '}
                  into the pantry, as the packs you bought — so what you don't
                  cook this week counts towards next week's plan.
                </>
              )}
            </p>
          </>
        )}
        {shopped.isError && (
          <p className="muted small">
            That didn't save. {(shopped.error as Error).message}
          </p>
        )}
        {list.supports_cart ? (
          /* "Order" was never true — this fills the store's online cart and
             nothing else. It now goes via a screen where the products can be
             corrected first, because a wrong match is silent. */
          <Link className="btn btn--primary btn--wide" to="/list/review">
            Add to {list.store} cart →
          </Link>
        ) : (
          <p className="muted small">
            {list.store} has no online cart we can push to — the list above is
            ready to shop from.
          </p>
        )}
        <button className="btn btn--wide" onClick={() => void clear()}>
          Uncheck everything
        </button>
      </div>
    </section>
  );
}
